import deepmerge from 'deepmerge';
import camelCase from 'lodash/camelCase';
import isEmpty from 'lodash/isEmpty';

import {
  VariantRules,
  VariantRulesSelectors,
  VariantRuleStylesheet,
} from '@candu-ai/styleguide/types';
import {
  customValuesToBeProcessed,
  rulesCrossReferences,
  styleguideSelectorsToCssSelectorIds,
} from 'craft/state/styleguide/constants';
import {
  CssSelectorId,
  EditorCssSelectorStylesheet,
  EditorStyleguide,
  EditorVariant,
} from 'craft/types';
import { unpackShorthandStyles } from 'craft/utils';

import { getVariable, isVariable } from './cssVariables';
import { BaseStylesheetRules } from './types';

const Global = 'Global';

const convertRules = (
  globalVariables: Record<string, string>,
  baseRules: Record<string, string>,
  variantRules: Record<string, string>,
) => {
  const combinedRules = { ...baseRules, ...variantRules };

  const getValue = (value: string): string => {
    // if it's drop shadow variable we should replace the variable with the value
    if (typeof value === 'string' && value?.includes('drop-shadow') && value?.includes('var')) {
      return getValue(`drop-shadow(${combinedRules['--drop-shadow']})`);
    }

    if (!isVariable(value)) {
      return value;
    }

    const variableName = getVariable(value);
    const global = globalVariables[variableName];
    if (global) {
      return global;
    }
    return getValue(combinedRules[variableName]);
  };

  const rules = {} as any;
  Object.entries(combinedRules).forEach(([key, value]) => {
    // custom values shouldn't be camel case
    if (customValuesToBeProcessed.includes(key)) {
      rules[key] = getValue(value);
      return;
    }

    // don't process variable
    if (key.indexOf('--') === 0) {
      return;
    }
    rules[camelCase(key)] = getValue(value);
  });

  return unpackShorthandStyles(rules) as any;
};

const processCrossReferenceRules = (
  type: string,
  cssSelectorRules: EditorCssSelectorStylesheet,
) => {
  const crossReference = rulesCrossReferences[type];
  if (!crossReference) {
    return;
  }

  Object.entries(crossReference as object).forEach(([destination, source]: CssSelectorId[]) => {
    if (isEmpty(cssSelectorRules[source])) {
      return;
    }
    // eslint-disable-next-line no-param-reassign
    cssSelectorRules[destination] = {
      ...cssSelectorRules[source],
      ...(cssSelectorRules[destination] ?? {}),
    };
  });
};

export const createEditorStyleguide = (
  baseStylesheetRules: BaseStylesheetRules[],
  variantRuleStylesheet: VariantRuleStylesheet,
): EditorStyleguide => {
  const globalVariables = variantRuleStylesheet.Global.base.selectors.global.rules;

  // @ts-expect-error paddleboard unavailable on localhost
  if (baseStylesheetRules.errorCode) {
    return {};
  }

  const convertVariantRule = (
    type: string,
    selectors: VariantRulesSelectors,
  ): EditorCssSelectorStylesheet => {
    const cssSelectorRules = {} as EditorCssSelectorStylesheet;

    Object.entries(selectors).forEach(([key, rule]) => {
      const baseVariant = baseStylesheetRules.find(
        (b) => b.componentName === type && b.key === key,
      );

      const hasNonRootCssSelectorId =
        styleguideSelectorsToCssSelectorIds[baseVariant?.selector || ''];
      // for now, don't process pseudo selectors
      if ((baseVariant?.selector ?? '').indexOf(':') > -1) {
        return;
      }
      const cssSelectorId = hasNonRootCssSelectorId || CssSelectorId.Root;
      cssSelectorRules[cssSelectorId] = convertRules(
        globalVariables,
        baseVariant?.rules || {},
        rule.rules,
      );
    });
    processCrossReferenceRules(type, cssSelectorRules);
    return cssSelectorRules;
  };

  const convertTypeRule = (type: string, rules: VariantRules) => {
    const variants = {} as EditorVariant;

    Object.entries(rules).forEach(([variant, variantRule]) => {
      variants[variant] = convertVariantRule(type, variantRule.selectors);
    });
    return variants;
  };

  const editorStyleguide = {} as EditorStyleguide;
  Object.entries(variantRuleStylesheet).forEach(([type, rules]) => {
    if (type === Global) {
      return;
    }
    let resolvedType = type;
    if (type === 'Link') {
      resolvedType = 'Typography';
    }
    editorStyleguide[resolvedType] = deepmerge(
      editorStyleguide[resolvedType] || {},
      convertTypeRule(type, rules),
    );
  });
  return editorStyleguide;
};
